import { themeColors } from '../../../../themeColors';
/**
 * @file   src\mobile\components\Onboarding\AgentProfile.js
 * @brief  This file is responsible for adding agent profile while onboarding.
 * @date   June, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */
import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import Button from "react-bootstrap/Button";
import TextArea from "../../../../components/Common/TextArea";
import TextBox from "../../../../components/Common/TextBox";
import FldCloseIcon from "../../../images/round-close-icon.svg";
import {
  handleTextBoxChange,
  capitalizeString,
} from "../../../../actions/common";
import AddIcon from "../../../../images/v2/Add-icon.svg";
import {
  capitalizeFirstString,
  getFileType,
  toTitleCase,
  getLocalStorage,
  trimString,
  formatPhoneNumber,
  upperCaseEachSentenceFirst,
  removeLocalStorage,
} from "../../../../helpers/common";
import {
  alphaNumericValidation,
  licenseValidation,
  phoneNumberValidation,
  textValidations,
} from "../../../../helpers/validations";
import {
  AGENT_USER_FREE_PLAN,
  BIO_MAX_COUNT,
  ERROR_TEXT,
  TESTIMONIAL_MAX_COUNT,
} from "../../../../constants/common";
import { fetchUserDetails } from "../../../../actions/userprofile";
import { USER_INFO_KEY } from "../../../../constants/common";
import Loader from "../../../../components/Common/Loader";
import { getAgentDetails } from "../../../../services/agentClientsService";
import ValidationAlertIcon from "../../../images/validation-alert-icon.svg";
import SparkleBigIcon from "../../../images/sparkle-big-icon.svg";
import ProfImgPlaceholder from "../../../images/prof-img-placeholder.svg";
import ProfileImageCropper from "../ProfileImageCropper";
import { getGeoLocation } from "../../../../services/commonServices";
import TopHeader from "../../common/TopHeader";

const AgentProfile = (props) => {
  const dispatch = useDispatch();
  const sessionData = getLocalStorage(USER_INFO_KEY);
  const userId = _.get(sessionData, "userInfo.userId", "");
  const userDetailsFromStore = useSelector(
    (state) => state.userProfile.userDetails,
  );
  const [userDetails, setUserDetails] = useState(null);
  const [location, setLocation] = useState("");
  const [locationError, setLocationError] = useState("");
  const locationString = getLocalStorage("LocationString");
  const [formattedAddress, setFormattedAddress] = useState();
  const [locationList, setLocationList] = useState([]);
  const [firstName, setFirstName] = useState("");
  const [firstNameError, setFNError] = useState("");
  const [lastName, setLastName] = useState("");
  const [lastNameError, setLNError] = useState("");
  const [company, setCompany] = useState("");
  const [companyError, setCompanyError] = useState("");
  const [license, setLicense] = useState("");
  const [licenseError, setLicenseError] = useState("");
  const [bio, setBio] = useState("");
  const [bioError, setBioError] = useState("");
  const [bioCount, setBioCount] = useState(0);
  const [imgError, setImgError] = useState("");
  const [reload, setReload] = useState(0);
  const supportedImageFormats = ["jpg", "jpeg", "png"];
  const { handleAgentDetailsSave } = props;
  const [isUploading, setIsUploading] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [logoError, setLogoError] = useState("");
  const [clientTestimonial, setTestimonials] = useState([]);
  const [testimonialCounts, setTestimonialCounts] = useState([0]);
  const [logoUrl, setLogoUrl] = useState("");
  const [facebookUrl, setFacebookUrl] = useState("");
  const [instagramUrl, setInstagramUrl] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [websiteUrlError, setWebsiteUrlError] = useState(false);
  const [youtubeUrlError, setYoutubeUrlError] = useState(false);
  const [facebookUrlError, setFacebookUrlError] = useState(false);
  const [instagramUrlError, setInstagramUrlError] = useState(false);
  const [bioCursorPos, setBioCursorPos] = useState(0);
  const bioRef = useRef();

  const [phone, setPhone] = useState("");
  const [phoneError, setPhoneError] = useState("");

  const [changedImage, setChangedImage] = useState(null);
  const [showPofileImageUploadModal, setShowPofileImageUploadModal] =
    useState(false);
  const [isSquareImage, setIsSquareImage] = useState(false);

  const ShowPofileImageUploadModalClose = () =>
    setShowPofileImageUploadModal(false);
  const ShowPofileImageUploadModalShow = (isSquareImage) => {
    setIsSquareImage(isSquareImage);
    setShowPofileImageUploadModal(true);
  };

  useEffect(() => {
    if (userId) {
      dispatch(fetchUserDetails());
    }
  }, [reload, userId]);

  useEffect(() => {
    if (userDetailsFromStore !== null && userDetailsFromStore?.length > 0) {
      setUserDetails(userDetailsFromStore[userDetailsFromStore?.length - 1]);
    }
  }, [userDetailsFromStore]);

  useEffect(() => {
    if (userId) {
      getAgentInfo(userId);
    }
  }, []);

  const getAgentInfo = async (userId) => {
    await getAgentDetails(userId).then((response) => {
      if (response) {
        const result = _.get(response, "result[0]", null);
        setLocation(_.get(result, "Location", null));
        setCompany(_.get(result, "Company", null));
        setLicense(_.get(result, "LicenseNo", null));
        setImageUrl(_.get(result, "Img", ""));
        setFirstName(_.get(result, "FirstName", null));
        setLastName(_.get(result, "LastName", null));
        setBio(_.get(result, "Bio", null));
        setFacebookUrl(_.get(result, "facebookUrl", null));
        setInstagramUrl(_.get(result, "instagramUrl", null));
        setWebsiteUrl(_.get(result, "websiteUrl", null));
        setYoutubeUrl(_.get(result, "youtubeUrl", null));
        setLogoUrl(_.get(result, "logoUrl", ""));
        setTestimonials(_.get(result, "clientTestimonial", []));
      }
    });
  };

  const getAddresseDetails = async ({ eventType, reqPayload }) => {
    await getGeoLocation(reqPayload).then((data) => {
      const results = _.get(data, "results");
      if (results?.length > 0) {
        const formatted_address = _.get(results?.[0], "formatted_address", "");
        if (results?.length > 0 && formatted_address !== "United States") {
          if (locationString) {
            handleItemSelection(results[0]);
            removeLocalStorage("LocationString");
          } else {
            setLocationList(results);
          }
        }
      }
    });
  };

  const clearAddressList = () => {
    setLocationList([]);
  };

  const handleLocationChange = (val) => {
    const value = capitalizeString(val);
    const reqPayload = `address=${value}&components=country:US`;
    if (value.trim() !== "") {
      getAddresseDetails({ reqPayload: reqPayload });
    } else {
      clearAddressList();
    }
    if (value && value.trim() !== "") {
      setLocation(value);
    } else {
      setLocation(value);
    }
  };

  const handleItemSelection = async (address) => {
    let city, state, county;
    removeLocalStorage("searchedLocation");
    const geometry = _.get(address, "geometry", "");
    const locBounds = _.get(geometry, "location", "");
    const formatted_address = _.get(address, "formatted_address", "");
    const firstAddressComponent = _.get(address, "address_components", "");
    const stateSearched = firstAddressComponent.filter(function (ca) {
      return ca.types?.[0] === "administrative_area_level_1";
    });
    if (stateSearched.length) {
      state = _.get(stateSearched?.[0], "short_name", "");
    }
    const locality = firstAddressComponent.filter(function (ca) {
      return ca.types?.[0] === "locality";
    });
    if (locality.length) {
      city = _.get(locality?.[0], "long_name", "");
    }
    const countyList = firstAddressComponent.filter(function (ca) {
      return ca.types?.[0] === "administrative_area_level_2";
    });

    if (countyList.length) {
      county = _.get(countyList?.[0], "short_name", "");
    }
    if (city != undefined) {
      setFormattedAddress(city + ", " + state);
    } else {
      setFormattedAddress(formatted_address);
    }
    setLocation(city != undefined ? city + ", " + state : formatted_address);
    clearAddressList();
  };

  const handleFirstNameChange = (e) => {
    setFNError("");
    const value = e.target.value;
    if (trimString(value) !== "") {
      if (!textValidations(value.trim())) {
        setFNError("Invalid input");
      } else {
        setFirstName(value);
      }
    } else {
      setFNError("Enter FirstName");
      setFirstName("");
    }
  };

  const handleLastNameChange = (e) => {
    setLNError("");
    const value = e.target.value;
    if (trimString(value) !== "") {
      if (!textValidations(value.trim())) {
        setLNError("Invalid input");
      } else {
        setLastName(value);
      }
    } else {
      setLNError("Enter LastName");
      setLastName("");
    }
  };

  const handleCompany = (value) => {
    setCompanyError("");
    if (trimString(value) !== "") {
      if (!alphaNumericValidation(value.trim())) {
        setCompanyError("Invalid input");
      } else {
        setCompany(value);
      }
    } else {
      setCompanyError("Enter Company");
      setCompany("");
    }
  };

  const handleLicenseChange = (value) => {
    setLicenseError("");
    setLicense(value);
    const trimmedValue = value.trim();
    if (trimmedValue === "") {
      setLicenseError("Enter License Number");
    } else if (!licenseValidation(trimmedValue)) {
      //V2 change for license validation
      setLicenseError("Invalid input");
    }
  };

  const handlePhone = (value) => {
    setPhoneError("");
    if (trimString(value) !== "") {
      value = formatPhoneNumber(value);
      setPhone(value);
    } else {
      setPhoneError("Enter Phone Number");
      setPhone("");
    }
  };

  const handleUrlChange = (url, type) => {
    const trimmedUrl = url?.trim() || ""; // Ensure it's a string
    const websiteUrlRegex =
      /^(https?:\/\/)?([\w\-]+\.)+[\w]{2,}(\/[\w\-._~:/?#[\]@!$&'()*+,;=]*)?$/;

    // Allow empty value, but validate only when there is input
    const isValid = trimmedUrl === "" || websiteUrlRegex.test(trimmedUrl);

    switch (type) {
      case "facebook":
        setFacebookUrl(trimmedUrl);
        setFacebookUrlError(trimmedUrl !== "" && !isValid); // Error only if input is invalid
        break;
      case "instagram":
        setInstagramUrl(trimmedUrl);
        setInstagramUrlError(trimmedUrl !== "" && !isValid);
        break;
      case "website":
        setWebsiteUrl(trimmedUrl);
        setWebsiteUrlError(trimmedUrl !== "" && !isValid);
        break;
      default:
        break;
    }
  };

  const handleYoutubeUrl = (url) => {
    const trimmedUrl = url.trim();
    setYoutubeUrl(trimmedUrl);
    setYoutubeUrlError(
      trimmedUrl === "" ? false : !validateYoutubeUrl(trimmedUrl),
    );
  };

  const validateYoutubeUrl = (url) => {
    const youtubeRegex =
      /^(https?:\/\/)?(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/)([^&]+)$/;
    return youtubeRegex.test(url);
  };

  const validatePhoneNumber = () => {
    setPhoneError("");
    if (trimString(phone) !== "") {
      if (!phoneNumberValidation(phone.trim())) {
        setPhoneError("Enter Valid Phone Number");
      } else {
        setPhone(phone);
      }
    } else {
      setPhoneError("Enter Phone Number");
      setPhone("");
    }
  };

  useEffect(() => {
    if (bioRef.current) {
      bioRef.current.setSelectionRange(bioCursorPos, bioCursorPos);
    }
  }, [bioCursorPos]);

  const handleBioChange = (e) => {
    setBioError("");
    const _bio = handleTextBoxChange(e);
    if (!textValidations(_bio)) {
      setBioError(ERROR_TEXT);
    }
    let value = capitalizeFirstString(_bio);
    value = upperCaseEachSentenceFirst(value);
    if (trimString(value) != "") {
      setBioCursorPos(e.target.selectionStart);
    }
    setBioCount(value.length);
    setBio(value);
  };

  const handleTestimonial = (e, index) => {
    const value = e.target.value;
    setTestimonials((prevTestimonials) =>
      prevTestimonials.map((testimonial, i) =>
        i === index ? value : testimonial,
      ),
    );
    setTestimonialCounts((prevCounts) =>
      prevCounts.map((count, i) => (i === index ? value.length : count)),
    );
  };

  const addTestimonial = () => {
    setTestimonials((prevTestimonials) => [...prevTestimonials, ""]);
    setTestimonialCounts((prevCounts) => [...prevCounts, 0]);
  };

  const updateReloadParam = () => {
    setReload(reload + 1);
  };

  const handleImageUpload = async (e, isLogo) => {
    const selectedFileType = getFileType(_.get(e.target.files[0], "name"));
    const file = e.target.files[0];

    setImgError("");
    let isValid = true;
    if (!_.includes(supportedImageFormats, selectedFileType)) {
      isValid = false;
      if (isLogo) setLogoError("Select Valid Logo");
      else setImgError("Select Valid Image");
    }
    if (isValid) {
      if (file) {
        const reader = new FileReader();
        reader.onload = () => {
          setChangedImage(reader.result);
          ShowPofileImageUploadModalShow(isLogo);
        };
        reader.readAsDataURL(file);
        // Reset the file input
        e.target.value = null;
      }
    }
  };

  const checkValid = () => {
    if (userDetails?.PlanName != AGENT_USER_FREE_PLAN) {
      if (location && license && company && firstName && lastName && phone) {
        if (
          locationError ||
          licenseError ||
          companyError ||
          firstNameError ||
          lastNameError ||
          phoneError
        ) {
          return true;
        }
        return false;
      } else {
        return true;
      }
    } else {
      if (location && license && company && firstName && lastName) {
        if (
          locationError ||
          licenseError ||
          companyError ||
          firstNameError ||
          lastNameError ||
          websiteUrlError ||
          youtubeUrlError
        ) {
          return true;
        }
        return false;
      } else {
        return true;
      }
    }
  };

  const agentInfo = {
    location: location,
    company: company,
    licenseNo: license,
    bio: bio,
    img: imageUrl || "",
    plan: 0,
    firstName: firstName,
    lastName: lastName,
    phone: phone,
    facebookUrl: facebookUrl,
    instagramUrl: instagramUrl,
    websiteUrl: websiteUrl,
    youtubeUrl: youtubeUrl,
    logoUrl: logoUrl,
    clientTestimonial: clientTestimonial,
  };

  return (
    <div className="gitcha-main-wrapper pb-0 bg-grey-variant">
      {isUploading ? <Loader /> : ""}
      <div className="content-wrapper pt-0 h-100">
        <TopHeader
          variant="review"
          outerClassname="bg-transparent d-none"
          topClassName="w-100"
          bottomClassName="signup-header-txt clearfix"
          allowPremiumOverride={false}
        >
        </TopHeader>
        <div className="inner-layout-content-new h-100">
          {userDetails &&
            userDetails?.PlanName &&
            userDetails?.PlanName === AGENT_USER_FREE_PLAN && (
              <div className="get-agent-wrap pt-5 clearfix">
                <h2>Let’s get your agent info</h2>
                <p className="pb-3 mb-0">
                  These details will be shown to clients after an introduction
                  has been accepted. It will remain anonymous until then.
                </p>
              </div>
            )}
          {userDetails &&
            userDetails?.PlanName &&
            userDetails?.PlanName !== AGENT_USER_FREE_PLAN && (
              <div className="welcome-premium-content">
                <h4 className="text-center pt-5">
                  Welcome to Pro Member{" "}
                  <img
                    className="align-text-baseline mb-1"
                    src={SparkleBigIcon}
                  />
                </h4>
                <p className="text-left">
                  With your Pro subscription, you can add a photo and bio to
                  your profile to help your introductions feel more
                  personalized. These details will be shown to clients after an
                  introduction has been accepted. It will remain anonymous until
                  then.
                </p>

                <div className="profile-wrap">
                  <div className="profile-wrap-img">
                    <figure>
                      {
                        <img
                          src={
                            imageUrl !== "" && imageUrl !== null
                              ? imageUrl + "?" + Math.random().toString(36)
                              : ProfImgPlaceholder
                          }
                        />
                      }
                    </figure>
                    <div className="file-upload">
                      <input
                        type="file"
                        name="profileImage"
                        id="file-3"
                        onChange={(e) => handleImageUpload(e)}
                        className="inputfile inputfile-3"
                        disabled={
                          userDetails?.PlanName !== AGENT_USER_FREE_PLAN
                            ? false
                            : true
                        }
                      />
                      <label for="file-3">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="12"
                          height="12"
                          viewBox="0 0 12 12"
                        >
                          <g transform="translate(-2 -2)">
                            <path
                              d="M2,8a.5.5,0,0,1,.5-.5h11a.5.5,0,0,1,0,1H2.5A.5.5,0,0,1,2,8Z"
                              fill="#fff"
                              fillRule="evenodd"
                            />
                            <path
                              d="M8,2a.5.5,0,0,1,.5.5v11a.5.5,0,0,1-1,0V2.5A.5.5,0,0,1,8,2Z"
                              fill="#fff"
                              fillRule="evenodd"
                            />
                          </g>
                        </svg>
                      </label>
                      <span className="error-msg w-125 h-auto">{imgError}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          <div className="d-block pe-3 ps-3">
            <div className="profile-wrap">
              {userDetails?.UserTypeId === 2 && userDetails?.period === 1 ? (
                <div className="profile-wrap-img">
                  <figure>
                    <img src={ProfImgPlaceholder} alt="" />
                  </figure>
                  <div className="file-upload">
                    <label for="file-3" className="bgGreen">
                      <img
                        className="align-text-baseline mb-1"
                        src={SparkleBigIcon}
                        alt=""
                      />
                    </label>
                  </div>
                </div>
              ) : (
                <div className="profile-wrap-img">
                  <figure className="d-flex align-items-center justify-content-center">
                    {imageUrl !== "" ? (
                      <img
                        src={imageUrl + "?" + Math.random().toString(36)}
                        alt=""
                      />
                    ) : (
                      <svg
                        width="24"
                        height="23"
                        viewBox="0 0 24 23"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M23.3643 21.1875C21.7317 18.375 19.1183 16.264 16.0253 15.2594C17.5078 14.3778 18.6599 13.0338 19.3046 11.4339C19.9493 9.83406 20.0508 8.06678 19.5937 6.40358C19.1365 4.74039 18.146 3.27329 16.7741 2.22766C15.4023 1.18204 13.7252 0.615723 12.0003 0.615723C10.2754 0.615723 8.59824 1.18204 7.22643 2.22766C5.85462 3.27329 4.86405 4.74039 4.4069 6.40358C3.94975 8.06678 4.05131 9.83406 4.69597 11.4339C5.34063 13.0338 6.49273 14.3778 7.97529 15.2594C4.88227 16.264 2.2689 18.375 0.636225 21.1875C0.556066 21.3194 0.513672 21.4707 0.513672 21.625C0.513672 21.7793 0.556066 21.9307 0.636225 22.0625C0.711012 22.1966 0.820597 22.308 0.953423 22.385C1.08625 22.462 1.23739 22.5018 1.39091 22.5H22.6097C22.7632 22.5018 22.9143 22.462 23.0472 22.385C23.18 22.308 23.2896 22.1966 23.3643 22.0625C23.4445 21.9307 23.4869 21.7793 23.4869 21.625C23.4869 21.4707 23.4445 21.3194 23.3643 21.1875Z"
                          fill={themeColors.neutral450}
                        />
                      </svg>
                    )}
                  </figure>
                  <div className="file-upload">
                    <input
                      type="file"
                      name="file-3[]"
                      id="file-3"
                      className="inputfile inputfile-3"
                      data-multiple-caption="{count} files selected"
                      multiple=""
                      onChange={(e) => handleImageUpload(e)}
                    />
                    <label for="file-3">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M11.6464 1.64645C11.8417 1.45118 12.1583 1.45118 12.3536 1.64645L14.3536 3.64645C14.5488 3.84171 14.5488 4.15829 14.3536 4.35355L8.35355 10.3536C8.25979 10.4473 8.13261 10.5 8 10.5H6C5.72386 10.5 5.5 10.2761 5.5 10V8C5.5 7.86739 5.55268 7.74021 5.64645 7.64645L11.6464 1.64645ZM6.5 8.20711V9.5H7.79289L13.2929 4L12 2.70711L6.5 8.20711Z"
                          fill="white"
                        />
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M10.1464 3.14645C10.3417 2.95118 10.6583 2.95118 10.8536 3.14645L12.8536 5.14645C13.0488 5.34171 13.0488 5.65829 12.8536 5.85355C12.6583 6.04882 12.3417 6.04882 12.1464 5.85355L10.1464 3.85355C9.95118 3.65829 9.95118 3.34171 10.1464 3.14645Z"
                          fill="white"
                        />
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M2.29289 2.29289C2.48043 2.10536 2.73478 2 3 2H8.5C8.77614 2 9 2.22386 9 2.5C9 2.77614 8.77614 3 8.5 3L3 3L3 13H13V7.5C13 7.22386 13.2239 7 13.5 7C13.7761 7 14 7.22386 14 7.5V13C14 13.2652 13.8946 13.5196 13.7071 13.7071C13.5196 13.8946 13.2652 14 13 14H3C2.73478 14 2.48043 13.8946 2.29289 13.7071C2.10536 13.5196 2 13.2652 2 13V3C2 2.73478 2.10536 2.48043 2.29289 2.29289Z"
                          fill="white"
                        />
                      </svg>
                    </label>
                  </div>
                </div>
              )}
            </div>

            <div className="row">
              <div className="col-6 pe-2">
                <div className="mob-fld-wrap position-relative mb-2">
                  <label className="form-label">
                    First name <span>*</span>
                  </label>

                  <TextBox
                    name="firstName"
                    type="text"
                    value={toTitleCase(firstName)}
                    placeholder="First name"
                    notShowError={true}
                    className={
                      firstNameError
                        ? "form-control mob-pr-44 validation-alert-brdr"
                        : "form-control mob-pr-44"
                    }
                    onChange={(e) => {
                      handleFirstNameChange(e);
                    }}
                  />
                  {firstName && !firstNameError && (
                    <Button
                      variant=""
                      className="fld-close-btn"
                      onClick={() => setFirstName("")}
                    >
                      <img src={FldCloseIcon} />
                    </Button>
                  )}
                  {firstNameError && (
                    <div className="validation-alert">
                      <img src={ValidationAlertIcon} />
                    </div>
                  )}

                  <div className="mob-error-msg">{firstNameError}</div>
                </div>
              </div>
              <div className="col-6 ps-2">
                <div className="mob-fld-wrap position-relative mb-0">
                  <label className="form-label">
                    Last name <span>*</span>
                  </label>

                  <TextBox
                    name="lastName"
                    type="text"
                    notShowError={true}
                    className={
                      lastNameError
                        ? "form-control mob-pr-44 validation-alert-brdr"
                        : "form-control mob-pr-44"
                    }
                    value={toTitleCase(lastName)}
                    placeholder="Last name"
                    onChange={(e) => {
                      handleLastNameChange(e);
                    }}
                  />
                  {lastName && !lastNameError && (
                    <Button
                      variant=""
                      className="fld-close-btn"
                      onClick={() => setLastName("")}
                    >
                      <img src={FldCloseIcon} />
                    </Button>
                  )}
                  {lastNameError && (
                    <div className="validation-alert">
                      <img src={ValidationAlertIcon} />
                    </div>
                  )}

                  <div className="mob-error-msg">{lastNameError}</div>
                </div>
              </div>
              <div className="col-12">
                <div className="mob-fld-wrap position-relative mb-0">
                  <label className="form-label">
                    Phone Number{" "}
                    {userDetails?.PlanName === AGENT_USER_FREE_PLAN ? (
                      <div className="pro-badge d-inline-flex align-items-center ms-1">
                        Pro
                      </div>
                    ) : (
                      <span> *</span>
                    )}
                  </label>

                  <TextBox
                    name="phone"
                    notShowError={true}
                    type="text"
                    className={
                      phoneError
                        ? "form-control mob-pr-44 validation-alert-brdr"
                        : "form-control mob-pr-44"
                    }
                    value={phone}
                    placeholder="Phone Number"
                    onChange={(e) => handlePhone(e.target.value)}
                    onBlur={() => {
                      validatePhoneNumber();
                    }}
                    disabled={
                      userDetails?.PlanName !== AGENT_USER_FREE_PLAN
                        ? false
                        : true
                    }
                    maxLength={14}
                    pattern="[0-9]*"
                    inputmode="numeric"
                  />
                  {phone && !phoneError && (
                    <Button
                      variant=""
                      className="fld-close-btn"
                      onClick={() => setLocation("")}
                    >
                      <img src={FldCloseIcon} />
                    </Button>
                  )}
                  {phoneError && (
                    <div className="validation-alert">
                      <img src={ValidationAlertIcon} />
                    </div>
                  )}

                  <div className="mob-error-msg">{phoneError}</div>
                </div>
              </div>
              <div className="col-12">
                <div className="mob-fld-wrap position-relative mb-0">
                  <label className="form-label">
                    Location <span>*</span>
                  </label>

                  <TextBox
                    name="location"
                    notShowError={true}
                    type="text"
                    className={
                      locationError
                        ? "form-control mob-pr-44 validation-alert-brdr"
                        : "form-control mob-pr-44"
                    }
                    value={toTitleCase(location)}
                    placeholder="City, State"
                    onChange={(e) => {
                      handleLocationChange(e.target.value);
                    }}
                    maxLength={40}
                  />
                  {/* {location && !locationError && (
                    <Button
                      variant=""
                      className="fld-close-btn"
                      onClick={() => setLocation("")}
                    >
                      <img src={FldCloseIcon} />
                    </Button>
                  )}
                  {locationError && (
                    <div className="validation-alert">
                      <img src={ValidationAlertIcon} />
                    </div>
                  )} */}
                  {locationList.length > 0 ? (
                    <div className="location-result-wrap mw-100 mt-4">
                      <ul>
                        {locationList?.map((address, i) => {
                          return (
                            <li key={i}>
                              <a
                                role="button"
                                href={void 0}
                                onClick={(e) => {
                                  handleItemSelection(address);
                                }}
                              >
                                {_.get(address, "formatted_address", "")}
                              </a>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ) : (
                    ""
                  )}

                  <div className="mob-error-msg">{locationError}</div>
                </div>
              </div>
              <div className="col-12">
                <div className="mob-fld-wrap position-relative mb-0">
                  <label className="form-label">
                    Company/Agency <span>*</span>
                  </label>

                  <TextBox
                    name="company"
                    type="text"
                    notShowError={true}
                    value={toTitleCase(company)}
                    placeholder="Company or agency name"
                    onChange={(e) => handleCompany(e.target.value)}
                    className={
                      companyError
                        ? "form-control mob-pr-44 validation-alert-brdr"
                        : "form-control mob-pr-44"
                    }
                    maxLength={50}
                  />
                  {company && !companyError && (
                    <Button
                      variant=""
                      className="fld-close-btn"
                      onClick={() => setCompany("")}
                    >
                      <img src={FldCloseIcon} />
                    </Button>
                  )}
                  {companyError && (
                    <div className="validation-alert">
                      <img src={ValidationAlertIcon} />
                    </div>
                  )}

                  <div className="mob-error-msg">{companyError}</div>
                </div>
              </div>
              <div
                className={`col-12 upload-btn-innerwrap addyour-property-details-wrap mb-12`}
              >
                <label
                  htmlFor="logoUrl"
                  className={`active agent-user-btn ${userDetails?.PlanName === AGENT_USER_FREE_PLAN ? "disabled" : ""}`}
                >
                  Add Logo
                </label>
                <input
                  type="file"
                  name="logoUrl"
                  id="logoUrl"
                  disabled={
                    userDetails?.PlanName !== AGENT_USER_FREE_PLAN
                      ? false
                      : true
                  }
                  onChange={(e) => handleImageUpload(e, true)}
                />

                {userDetails?.PlanName === AGENT_USER_FREE_PLAN ? (
                  <div className="pro-badge d-inline-flex align-items-center ms-1">
                    Pro
                  </div>
                ) : null}
                <span className="error-msg w-104 h-auto error-wordbreak">
                  {logoError}
                </span>
              </div>
              <div className="col-12">
                <div className="mob-fld-wrap position-relative mb-2">
                  <label className="form-label">
                    License number <span>*</span>
                  </label>

                  <TextBox
                    name="company"
                    type="text"
                    notShowError={true}
                    className={
                      licenseError
                        ? "form-control mob-pr-44 validation-alert-brdr"
                        : "form-control mob-pr-44"
                    }
                    value={toTitleCase(license)}
                    placeholder="License number"
                    onChange={(e) => handleLicenseChange(e.target.value)}
                    maxLength={40}
                  />
                  {license && !licenseError && (
                    <Button
                      variant=""
                      className="fld-close-btn"
                      onClick={() => setLicense("")}
                    >
                      <img src={FldCloseIcon} />
                    </Button>
                  )}
                  {licenseError && (
                    <div className="validation-alert">
                      <img src={ValidationAlertIcon} />
                    </div>
                  )}

                  <div className="mob-error-msg">{licenseError}</div>
                </div>
              </div>
              <div className="col-12">
                <div className="mob-fld-wrap position-relative mb-0">
                  <label className="form-label">
                    Bio
                    {userDetails?.PlanName === AGENT_USER_FREE_PLAN ? (
                      <div className="pro-badge d-inline-flex align-items-center ms-1">
                        Pro
                      </div>
                    ) : null}
                  </label>
                  <TextArea
                    disabled={
                      userDetails?.PlanName !== AGENT_USER_FREE_PLAN
                        ? false
                        : true
                    }
                    className="form-control"
                    errorMessage={bioError}
                    maxLength={500}
                    value={bio ? bio : ""}
                    placeholder="Tell other agents and prospective clients a little bit about yourself..."
                    onChange={(e) => {
                      handleBioChange(e);
                    }}
                    txtRef={bioRef}
                  />
                  <div className="des-count mb-0">
                    <span>{bioCount}</span>/<span>{BIO_MAX_COUNT}</span>
                  </div>
                  <br></br> <br></br>
                </div>
              </div>
              <div className="col-12">
                <div className="mob-fld-wrap position-relative mb-0">
                  <label className="form-label">
                    Client testimonial (optional)
                    {userDetails?.PlanName === AGENT_USER_FREE_PLAN ? (
                      <div className="pro-badge d-inline-flex align-items-center ms-1">
                        Pro
                      </div>
                    ) : null}
                  </label>
                  <TextArea
                    disabled={
                      userDetails?.PlanName !== AGENT_USER_FREE_PLAN
                        ? false
                        : true
                    }
                    className="form-control"
                    maxLength={1000}
                    value={clientTestimonial}
                    placeholder="What you're clients think..."
                    onChange={(e) => {
                      handleTestimonial(e, index);
                    }}
                  />
                  <div className="des-count mb-0">
                    <span>{testimonialCounts}</span>/
                    <span>{TESTIMONIAL_MAX_COUNT}</span>
                  </div>
                </div>
              </div>
              <div
                className="d-flex align-items-center add-testimonial-btn"
                onClick={addTestimonial}
              >
                <img src={AddIcon} alt="add-icon"></img>
                <button
                  className="add-another-testimonial"
                  disabled={
                    userDetails?.PlanName !== AGENT_USER_FREE_PLAN
                      ? false
                      : true
                  }
                >
                  Add another testimonial
                </button>
              </div>
              <div className="col-12">
                <div className="mob-fld-wrap position-relative mb-2">
                  <label className="form-label">
                    Facebook Profile (Optional)
                    {userDetails?.PlanName === AGENT_USER_FREE_PLAN ? (
                      <div className="pro-badge d-inline-flex align-items-center ms-1">
                        Pro
                      </div>
                    ) : null}
                  </label>

                  <TextBox
                    name="facebookUrl"
                    type="text"
                    notShowError={true}
                    className={"form-control mob-pr-44"}
                    value={facebookUrl}
                    disabled={
                      userDetails?.PlanName !== AGENT_USER_FREE_PLAN
                        ? false
                        : true
                    }
                    placeholder="Paste link to profile here..."
                    onChange={(e) =>
                      handleUrlChange(e.target.value, "facebook")
                    }
                    errorMessage={facebookUrlError ? "Enter Valid URl" : ""}
                  />
                </div>
              </div>
              <div className="col-12">
                <div className="mob-fld-wrap position-relative mb-2">
                  <label className="form-label">
                    Instagram Profile (Optional)
                    {userDetails?.PlanName === AGENT_USER_FREE_PLAN ? (
                      <div className="pro-badge d-inline-flex align-items-center ms-1">
                        Pro
                      </div>
                    ) : null}
                  </label>

                  <TextBox
                    name="instagramUrl"
                    type="text"
                    disabled={
                      userDetails?.PlanName !== AGENT_USER_FREE_PLAN
                        ? false
                        : true
                    }
                    notShowError={true}
                    className={"form-control mob-pr-44"}
                    value={instagramUrl}
                    placeholder="Paste link to profile here..."
                    onChange={(e) =>
                      handleUrlChange(e.target.value, "instagram")
                    }
                    errorMessage={instagramUrlError ? "Enter Valid URl" : ""}
                  />
                </div>
              </div>
              <div className="col-12">
                <div className="mob-fld-wrap position-relative mb-2">
                  <label className="form-label">Website</label>

                  <TextBox
                    name="websiteUrl"
                    type="text"
                    notShowError={true}
                    className={"form-control mob-pr-44"}
                    value={websiteUrl}
                    placeholder="Paste link to website here..."
                    onChange={(e) => handleUrlChange(e.target.value, "website")}
                    errorMessage={websiteUrlError ? "Enter Valid URl" : ""}
                  />
                </div>
              </div>
              <div className="col-12">
                <div className="mob-fld-wrap position-relative mb-0">
                  <label className="form-label">
                    Youtube Video
                    {userDetails?.PlanName === AGENT_USER_FREE_PLAN ? (
                      <div className="pro-badge d-inline-flex align-items-center ms-1">
                        Pro
                      </div>
                    ) : null}
                  </label>

                  <TextBox
                    name="youtubeUrl"
                    type="text"
                    notShowError={true}
                    disabled={
                      userDetails?.PlanName !== AGENT_USER_FREE_PLAN
                        ? false
                        : true
                    }
                    className={"form-control mob-pr-44"}
                    value={youtubeUrl}
                    placeholder="Paste video embed link here"
                    onChange={(e) => handleYoutubeUrl(e.target.value)}
                    errorMessage={
                      youtubeUrlError ? "Enter Valid Youtube URl" : ""
                    }
                  />
                </div>
              </div>
            </div>
            <div className="btn-wrap text-center pb-4 pt-2">
              <button
                type="button"
                className="btn btn-primary w-100 mb-0"
                disabled={checkValid()}
                onClick={() => handleAgentDetailsSave(agentInfo)}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      </div>

      <ProfileImageCropper
        userId={userId}
        image={changedImage}
        show={showPofileImageUploadModal}
        onHide={() => {
          ShowPofileImageUploadModalClose();
        }}
        isSquare={isSquareImage}
        updateImageAfterCrop={(imageUrl) => {
          if (isSquareImage) {
            setLogoUrl(imageUrl);
          } else {
            setImageUrl(imageUrl);
          }
          ShowPofileImageUploadModalClose();
        }}
      />
    </div>
  );
};

export default AgentProfile;
